import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import LiveEmptyState from "../components/LiveEmptyState";
import useLiveMode from "../hooks/useLiveMode";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { Upload, RefreshCcw, Plus, X, History, Phone, Globe, FileSpreadsheet, FileText, UserPlus, PhoneIncoming, Search } from "lucide-react";
import { toast } from "sonner";
import { useGlobalDate } from "../contexts/GlobalDateContext";

const EMPTY = {
  name: "", phone: "", city: "", problem: "",
  date: new Date().toISOString().slice(0, 10),
  status: "new", client_id: "", campaign_id: "", source: "manual", relevant: true,
};

const SOURCE_META = {
  google_ads_call_lead: { label: "Phone call · Google Ads", color: "hsl(38 92% 45%)", Icon: PhoneIncoming },
  google_sheets:        { label: "Google Sheets",           color: "hsl(150 70% 35%)", Icon: FileSpreadsheet },
  csv_upload:           { label: "CSV upload",              color: "hsl(220 60% 50%)", Icon: FileText },
  manual:               { label: "Manual",                  color: "hsl(280 60% 50%)", Icon: UserPlus },
  website:              { label: "Website",                 color: "hsl(190 60% 40%)", Icon: Globe },
  seed:                 { label: "Demo",                    color: "hsl(220 10% 50%)", Icon: FileText },
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filterValues, setFilterValues] = useState({ sources: [], cities: [] });

  // Filter state
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Date range comes from the global top-bar dropdown.
  const { range: dateRange, customFrom, customTo, from: globalFrom, to: globalTo } = useGlobalDate();
  const [clientFilter, setClientFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);

  // Pagination
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);  // 1-indexed

  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const { isLive, isConnected } = useLiveMode();

  // v1.4.2 filter state — channel/lead-type, qualified calls, whatsapp.
  const [leadTypeFilter, setLeadTypeFilter] = useState("");  // "" or "search,pmax,gbp..."
  const [officialOnly, setOfficialOnly] = useState(false);
  const [qualifiedOnly, setQualifiedOnly] = useState(false);
  const [whatsappOnly, setWhatsappOnly] = useState(false);

  const buildParams = () => {
    const params = { limit: pageSize, offset: (page - 1) * pageSize };
    if (searchQ.trim()) params.q = searchQ.trim();
    if (statusFilter) params.status = statusFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (campaignFilter) params.campaign_id = campaignFilter;
    if (sourceFilter) params.source = sourceFilter;
    if (assigneeFilter) params.assignee_user_id = assigneeFilter;
    if (cityFilter) params.city = cityFilter;
    if (duplicatesOnly) params.duplicates_only = true;
    if (leadTypeFilter) params.lead_type = leadTypeFilter;
    if (officialOnly) params.official_only = true;
    if (qualifiedOnly) params.qualified_calls_only = true;
    if (whatsappOnly) params.whatsapp_only = true;
    if (dateRange === "custom") {
      if (customFrom) params.date_from = customFrom;
      if (customTo) params.date_to = customTo;
    } else if (dateRange !== "all") {
      params.date_range = dateRange;
    }
    return params;
  };

  // Stats query mirrors the list query but excludes the active status pick
  // (we *want* the per-status breakdown) so the pill counts stay in sync
  // with the filtered table.
  const buildStatsParams = () => {
    const p = buildParams();
    delete p.status;
    delete p.duplicates_only;
    delete p.limit;
    delete p.offset;
    return p;
  };

  const summaryRange = dateRange === "all" || dateRange === "custom" ? "last_30d" : dateRange;

  const load = () => {
    api.get("/leads", { params: buildParams() }).then((r) => {
      setLeads(r.data.leads);
      setTotal(r.data.total);
    }).catch(() => { setLeads([]); setTotal(0); });
    api.get("/leads/stats", { params: buildStatsParams() }).then((r) => setStats(r.data)).catch(() => {});
    const summaryParams = { date_range: summaryRange };
    if (clientFilter) summaryParams.client_id = clientFilter;
    api.get("/leads/total-summary", { params: summaryParams }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    searchQ,
    statusFilter, dateRange, customFrom, customTo,
    clientFilter, campaignFilter, sourceFilter, assigneeFilter, cityFilter, duplicatesOnly,
    leadTypeFilter, officialOnly, qualifiedOnly, whatsappOnly,
    page, pageSize,
  ]);

  // Filter changes should reset to page 1 — otherwise the user can land on
  // an empty page when narrowing the result set.
  useEffect(() => {
    setPage(1);
  }, [searchQ, statusFilter, dateRange, customFrom, customTo, clientFilter, campaignFilter, sourceFilter, assigneeFilter, cityFilter, duplicatesOnly, leadTypeFilter, officialOnly, qualifiedOnly, whatsappOnly, pageSize]);

  useEffect(() => {
    api.get("/admin/lead-statuses").then((r) => setStatuses(r.data.statuses || [])).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    api.get("/leads/filter-values/all").then((r) => setFilterValues(r.data)).catch(() => {});
  }, []);

  const upload = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/leads/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} leads`);
      load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const create = async () => {
    if (!form.name && !form.phone) return toast.error("Name or phone required");
    const payload = { ...form };
    ["client_id", "campaign_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
    await api.post("/leads", payload);
    toast.success("Lead created");
    setForm(null);
    load();
  };

  const patch = async (id, body) => {
    await api.patch(`/leads/${id}`, body);
    load();
    if (detail && detail.id === id) openDetail(id);
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/leads/${id}`);
    setDetail(data);
    setNote("");
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${detail.id}/notes`, { note });
    setNote("");
    openDetail(detail.id);
    toast.success("Note added");
  };

  const resetFilters = () => {
    setSearchQ(""); setStatusFilter("");
    setClientFilter(""); setCampaignFilter(""); setSourceFilter(""); setAssigneeFilter("");
    setCityFilter(""); setDuplicatesOnly(false);
    setLeadTypeFilter(""); setOfficialOnly(false); setQualifiedOnly(false); setWhatsappOnly(false);
    // Note: global date range is intentionally NOT reset here — it's
    // controlled from the top-bar dropdown and shared across all pages.
  };

  const activeFilterCount = useMemo(() => [
    searchQ.trim(), statusFilter, clientFilter, campaignFilter, sourceFilter, assigneeFilter, cityFilter,
    duplicatesOnly ? "dup" : "", leadTypeFilter,
    officialOnly ? "off" : "", qualifiedOnly ? "qc" : "", whatsappOnly ? "wa" : "",
  ].filter(Boolean).length, [searchQ, statusFilter, clientFilter, campaignFilter, sourceFilter, assigneeFilter, cityFilter, duplicatesOnly, leadTypeFilter, officialOnly, qualifiedOnly, whatsappOnly]);

  const filteredCampaigns = clientFilter ? campaigns.filter((c) => c.client_id === clientFilter) : campaigns;
  const sources = filterValues.sources?.length ? filterValues.sources : Object.keys(SOURCE_META);

  return (
    <Layout
      title="Leads"
      subtitle={`${total} matching · ${stats?.relevant || 0} relevant overall`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setForm({ ...EMPTY })} data-testid="new-lead-btn" className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-sm"><Plus className="w-3.5 h-3.5" /> New Lead</button>
          <button onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn" className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium border border-border bg-card hover:bg-secondary rounded-sm">
            <Upload className="w-3.5 h-3.5" /> Upload CSV
          </button>
          <input type="file" accept=".csv" hidden ref={fileRef} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <button onClick={load} data-testid="leads-refresh" className="h-9 w-9 flex items-center justify-center border border-border bg-card hover:bg-secondary rounded-sm"><RefreshCcw className="w-3.5 h-3.5" /></button>
        </div>
      }
    >
      {/* Total summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4" data-testid="leads-summary-tiles">
          <SummaryTile label="Total" value={summary.total} highlight hint="All leads in the selected date range across all sources." />
          <SummaryTile
            label="Official (Search)"
            value={summary.official_leads ?? 0}
            highlight
            accent="hsl(150 70% 35%)"
            hint="Marketing-team rule: only Search Ads leads count as official. Toggle the 'Official only' filter to constrain the table."
          />
          <SummaryTile
            label="Website"
            value={summary.website}
            icon={Globe}
            hint={`Leads with source="website" — typically posted to /api/leads/webhook from your site forms or PHP backend. ${summary.website === 0 ? "Currently 0 because no website form is wired to the webhook yet." : ""}`}
          />
          <SummaryTile label="Sheets" value={summary.google_sheets} icon={FileSpreadsheet} hint="Leads ingested from your mapped Google Sheets tabs." />
          <SummaryTile label="Phone calls" value={summary.phone_calls} icon={PhoneIncoming} accent="hsl(38 92% 45%)" hint="Phone-call leads — either per-call rows from Google Ads call extensions or aggregate phone_calls metric per day." />
          <SummaryTile
            label="Qualified calls"
            value={summary.qualified_call_leads ?? 0}
            icon={Phone}
            accent="hsl(150 70% 35%)"
            hint="Call leads where call_duration > 15 seconds. Empty until your sheet maps a 'Call duration' column or Google Ads exposes per-call data."
          />
          <SummaryTile
            label="WhatsApp clicks"
            value={summary.whatsapp_clicks ?? 0}
            icon={Globe}
            accent="hsl(150 70% 35%)"
            hint={`Leads with whatsapp_click=true. ${(summary.whatsapp_clicks ?? 0) === 0 ? "Empty state: WhatsApp click data is not connected yet — map a 'WhatsApp click' column in your Sheets connector to ingest it." : ""}`}
          />
        </div>
      )}

      {/* Search bar */}
      <div className="relative mb-3" data-testid="leads-search-bar">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
        <input
          data-testid="leads-search-input"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search by name, phone, campaign or city…"
          className="w-full h-10 pl-9 pr-9 border border-border bg-card rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
        {searchQ && (
          <button onClick={() => setSearchQ("")} className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="leads-search-clear">
            <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-card border border-border rounded-sm p-3 mb-4" data-testid="leads-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-9 px-3 inline-flex items-center gap-1.5 border border-border rounded-sm text-xs bg-secondary/40" title="Date range is set from the top-bar — applies across all pages">
            <span className="text-muted-foreground">📅</span>
            <span className="font-medium">{dateRange === "custom" ? `${customFrom || globalFrom} → ${customTo || globalTo}` : dateRange.replace(/_/g, " ")}</span>
          </div>
          <select value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setCampaignFilter(""); }} data-testid="leads-filter-client" className="h-9 px-3 border border-border rounded-sm text-sm min-w-[140px]">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="leads-filter-campaign" className="h-9 px-3 border border-border rounded-sm text-sm min-w-[140px]">
            <option value="">All campaigns</option>
            {filteredCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="leads-filter-source" className="h-9 px-3 border border-border rounded-sm text-sm">
            <option value="">All sources</option>
            {sources.map((s) => <option key={s} value={s}>{SOURCE_META[s]?.label || s}</option>)}
          </select>

          <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} data-testid="leads-filter-assignee" className="h-9 px-3 border border-border rounded-sm text-sm">
            <option value="">All assignees</option>
            <option value="unassigned">Unassigned</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} data-testid="leads-filter-city" className="h-9 px-3 border border-border rounded-sm text-sm">
            <option value="">All cities</option>
            {(filterValues.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <label className="inline-flex items-center gap-1.5 text-xs h-9 px-3 border border-border rounded-sm bg-card cursor-pointer">
            <input type="checkbox" checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} data-testid="leads-filter-duplicates" />
            Duplicates only
          </label>

          <select value={leadTypeFilter} onChange={(e) => setLeadTypeFilter(e.target.value)} data-testid="leads-filter-lead-type" className="h-9 px-3 border border-border rounded-sm text-sm">
            <option value="">All channels</option>
            <option value="search">Search Ads</option>
            <option value="pmax">PMAX</option>
            <option value="gbp">GBP</option>
            <option value="boosting">Boosting</option>
            <option value="other">Other</option>
          </select>

          <label className="inline-flex items-center gap-1.5 text-xs h-9 px-3 border border-border rounded-sm bg-card cursor-pointer" title="Marketing-team rule: only Search Ads count as official leads.">
            <input type="checkbox" checked={officialOnly} onChange={(e) => setOfficialOnly(e.target.checked)} data-testid="leads-filter-official" />
            Official only
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs h-9 px-3 border border-border rounded-sm bg-card cursor-pointer" title="Calls > 15 seconds.">
            <input type="checkbox" checked={qualifiedOnly} onChange={(e) => setQualifiedOnly(e.target.checked)} data-testid="leads-filter-qualified" />
            Qualified calls
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs h-9 px-3 border border-border rounded-sm bg-card cursor-pointer">
            <input type="checkbox" checked={whatsappOnly} onChange={(e) => setWhatsappOnly(e.target.checked)} data-testid="leads-filter-whatsapp" />
            WhatsApp only
          </label>

          {activeFilterCount > 0 && (
            <button onClick={resetFilters} data-testid="leads-filter-reset" className="h-9 px-3 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Status pills */}
      {stats && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setStatusFilter("")} data-testid="lead-filter-all" className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${statusFilter === "" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>All ({stats.total})</button>
          {statuses.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              data-testid={`lead-filter-${s.key}`}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${statusFilter === s.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
            >
              {s.label} ({stats.by_status[s.key] || 0})
            </button>
          ))}
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="leads-table">
        {isLive && total === 0 && activeFilterCount === 0 && !statusFilter ? (
          <LiveEmptyState
            entity="leads"
            variant={isConnected ? "module" : "connector"}
            compact
            title={isConnected ? "No leads received yet" : "No live leads yet"}
            message={isConnected
              ? "Lead rows appear here from these sources: form submissions, Google Sheets, CSV uploads, manual entries, and Google Ads phone calls."
              : "Upload a CSV, create a lead manually, or connect Google Sheets to start seeing real leads here."}
            actions={isConnected ? (
              <>
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="leads-empty-upload-csv"
                  className="inline-flex items-center gap-1.5 h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90"
                >Upload CSV</button>
                <button
                  onClick={() => setForm(EMPTY)}
                  data-testid="leads-empty-new-lead"
                  className="inline-flex items-center gap-1.5 h-9 px-4 border border-border text-sm rounded-sm hover:bg-secondary"
                >New lead manually</button>
                <Link
                  to="/admin?tab=connectors"
                  data-testid="leads-empty-connect-sheets"
                  className="inline-flex items-center gap-1.5 h-9 px-4 border border-border text-sm rounded-sm hover:bg-secondary"
                >Connect Google Sheets</Link>
              </>
            ) : undefined}
            ctaHref="/admin?tab=connectors"
            ctaLabel="Open connectors"
          />
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th style={{ width: 110 }}>Date</th>
                <th style={{ width: 80 }}>Time</th>
                <th style={{ width: 90 }}>Duration</th>
                <th>Campaign</th>
                <th style={{ width: 130 }}>Source</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 130 }}>Assignee</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const isCall = l.source === "google_ads_call_lead";
                const sm = SOURCE_META[l.source] || { label: l.source, color: "hsl(220 10% 40%)" };
                const SrcIcon = sm.Icon || Phone;
                return (
                  <tr key={l.id} className={`cursor-pointer hover:bg-secondary/30 ${l.looks_like_header ? "bg-[hsl(38_92%_50%)]/5" : ""}`} onClick={() => openDetail(l.id)} data-testid={`lead-row-${l.id}`}>
                    <td className="font-medium">
                      {l.looks_like_header && <span title="Suspected sheet-header row — flagged by v1.4.1; admin can clean from Connectors → Sheets." className="mr-1.5 text-[hsl(38_92%_45%)] text-[10px] uppercase font-mono">⚠ HDR</span>}
                      {isCall && <Phone className="w-3.5 h-3.5 inline-block mr-1.5 text-[hsl(38_92%_45%)]" />}
                      {l.name || "—"}
                    </td>
                    <td className="font-mono text-xs">{l.phone}</td>
                    <td>{l.lead_date || l.date ? formatDate(l.lead_date || l.date) : <span className="text-muted-foreground italic text-[11px]">unknown</span>}</td>
                    <td className="font-mono text-[11px] text-muted-foreground">{l.time || "—"}</td>
                    <td className="font-mono text-[11px] tabular">
                      {isCall && l.duration_seconds != null ? `${l.duration_seconds}s` : "—"}
                    </td>
                    <td className="text-xs truncate max-w-[180px]">{l.page_title || l.city || "—"}</td>
                    <td>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm"
                        style={{ color: sm.color, backgroundColor: `${sm.color}15` }}
                        data-testid={`lead-source-${l.id}`}
                      >
                        <SrcIcon className="w-3 h-3" />
                        <span className="truncate max-w-[80px]">{sm.label.split("·")[0].trim()}</span>
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select value={l.status} onChange={(e) => patch(l.id, { status: e.target.value })} className="bg-transparent border border-border text-[11px] rounded-sm px-1 py-0.5 max-w-[110px]" data-testid={`lead-status-${l.id}`}>
                        {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select value={l.assignee_user_id || ""} onChange={(e) => patch(l.id, { assignee_user_id: e.target.value || null })} className="bg-transparent border border-border text-[11px] rounded-sm px-1 py-0.5">
                        <option value="">Unassigned</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </td>
                    <td><button onClick={(e) => { e.stopPropagation(); openDetail(l.id); }} className="text-xs text-primary hover:underline">Open →</button></td>
                  </tr>
                );
              })}
              {leads.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-sm text-muted-foreground">No leads match the current filters.</td></tr>}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {leads.length > 0 && (
          <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-between gap-3" data-testid="leads-pagination">
            <div className="text-xs text-muted-foreground">
              Showing <span className="font-semibold tabular text-foreground">{(page - 1) * pageSize + 1}</span>
              {" – "}
              <span className="font-semibold tabular text-foreground">{Math.min(page * pageSize, total)}</span>
              {" of "}
              <span className="font-semibold tabular text-foreground">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-muted-foreground">Per page</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                data-testid="leads-page-size"
                className="h-8 px-2 border border-border rounded-sm text-xs bg-card"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="leads-page-prev"
                className="h-8 px-3 text-xs border border-border rounded-sm hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-xs tabular px-2">
                Page <span className="font-semibold">{page}</span> / {Math.max(1, Math.ceil(total / pageSize))}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * pageSize >= total}
                data-testid="leads-page-next"
                className="h-8 px-3 text-xs border border-border rounded-sm hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create lead modal */}
      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="lead-form-modal">
            <div className="flex justify-between items-start mb-4"><div className="font-heading text-lg font-medium">New Lead</div><button onClick={() => setForm(null)}><X className="w-4 h-4" /></button></div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="lead-name" />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="lead-phone" />
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" />
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" />
              </div>
              <textarea placeholder="Problem / need" value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} className="w-full h-16 px-3 py-2 border border-border rounded-sm text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
                  <option value="">No client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={form.campaign_id || ""} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
                  <option value="">No campaign</option>
                  {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
                  {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm px-3"><input type="checkbox" checked={form.relevant} onChange={(e) => setForm({ ...form, relevant: e.target.checked })} /> Relevant</label>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={create} data-testid="lead-create-btn" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Create</button>
              <button onClick={() => setForm(null)} className="flex-1 h-10 border border-border text-sm rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-heading text-lg font-medium flex items-center gap-2">
                  {detail.source === "google_ads_call_lead" && <Phone className="w-4 h-4 text-[hsl(38_92%_45%)]" />}
                  {detail.name || "Unnamed lead"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {detail.phone} · {detail.city || "—"} · {formatDate(detail.date)}
                  {detail.time && <> · {detail.time}</>}
                  {detail.duration_seconds != null && <> · {detail.duration_seconds}s</>}
                </div>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-4 h-4" /></button>
            </div>

            {/* Call-specific block */}
            {detail.source === "google_ads_call_lead" && (
              <div className="border border-[hsl(38_92%_45%)]/30 bg-[hsl(38_92%_45%)]/5 rounded-sm p-3 mb-4">
                <div className="label-mono text-[hsl(38_92%_45%)] mb-2">Call detail</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Cell label="Date" value={formatDate(detail.date)} />
                  <Cell label="Time" value={detail.time || "—"} />
                  <Cell label="Duration" value={detail.duration_seconds != null ? `${detail.duration_seconds}s` : "—"} />
                  <Cell label="Status" value={detail.call_status || "—"} />
                  <Cell label="Type" value={detail.call_type || "—"} />
                  <Cell label="Where shown" value={detail.tracking_display_location || "—"} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Labeled label="Status">
                <select value={detail.status} onChange={(e) => patch(detail.id, { status: e.target.value })} className="w-full h-9 px-2 border border-border rounded-sm text-sm" data-testid="lead-detail-status">
                  {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </Labeled>
              <Labeled label="Assignee">
                <select value={detail.assignee_user_id || ""} onChange={(e) => patch(detail.id, { assignee_user_id: e.target.value || null })} className="w-full h-9 px-2 border border-border rounded-sm text-sm">
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Labeled>
              <Labeled label="Relevant">
                <select value={String(detail.relevant)} onChange={(e) => patch(detail.id, { relevant: e.target.value === "true" })} className="w-full h-9 px-2 border border-border rounded-sm text-sm">
                  <option value="true">Yes</option><option value="false">No</option>
                </select>
              </Labeled>
              <Labeled label="Source">
                <div className="text-sm py-2">{SOURCE_META[detail.source]?.label || detail.source || "—"}</div>
              </Labeled>
            </div>

            {detail.problem && (
              <div className="mb-4">
                <div className="label-mono mb-1">Problem / need</div>
                <div className="text-sm bg-secondary/20 border border-border rounded-sm p-3">{detail.problem}</div>
              </div>
            )}

            <div className="label-mono mb-2">Add note</div>
            <div className="flex gap-2 mb-4">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Activity note…" className="flex-1 h-9 px-3 border border-border rounded-sm text-sm" data-testid="lead-note-input" />
              <button onClick={addNote} data-testid="lead-add-note-btn" className="h-9 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm">Add</button>
            </div>

            <div className="flex items-center gap-2 mb-2"><History className="w-4 h-4 text-muted-foreground" /><div className="label-mono">Status & Activity History</div></div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {(detail.history || []).map((h) => (
                <div key={h.id} className="text-xs py-1.5 border-b border-border last:border-b-0 flex justify-between">
                  <span>
                    <b>{h.action}</b>
                    {h.before && <> · {h.before} → {h.after}</>}
                    {h.note && <span className="text-muted-foreground"> · {h.note}</span>}
                  </span>
                  <span className="text-muted-foreground">{h.user_name} · {formatDate(h.at)}</span>
                </div>
              ))}
              {(!detail.history || detail.history.length === 0) && <div className="text-xs text-muted-foreground">No activity yet.</div>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Labeled({ label, children }) {
  return (<div><div className="label-mono mb-1">{label}</div>{children}</div>);
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, accent, highlight, hint }) {
  return (
    <div
      className={`bg-card border rounded-sm px-3 py-2.5 ${highlight ? "border-l-2 border-l-primary" : "border-border"} ${hint ? "cursor-help" : ""}`}
      style={accent ? { borderLeft: `2px solid ${accent}` } : {}}
      title={hint || undefined}
    >
      <div className="flex items-center justify-between mb-0.5">
        <div className="label-mono">{label}</div>
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" />}
      </div>
      <div className="text-xl font-semibold tabular leading-tight">{value ?? 0}</div>
    </div>
  );
}
